import { Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType } from 'docx';

// Type definitions for docx elements
type DocxElement = Paragraph | Table | TextRun;

// Sanitize text to remove invalid characters that can corrupt Word documents
const sanitizeText = (text: string): string => {
  if (!text) return ' ';
  // Remove null bytes and other control characters except newlines, carriage returns, and tabs
  let sanitized = '';
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const code = char.charCodeAt(0);
    // Keep printable characters, newline (10), carriage return (13), and tab (9)
    // Remove null (0) and other control characters (1-8, 11-12, 14-31, 127)
    if (
      code === 0 ||
      (code >= 1 && code <= 8) ||
      code === 11 ||
      code === 12 ||
      (code >= 14 && code <= 31) ||
      code === 127
    ) {
      continue; // Skip control characters
    }
    sanitized += char;
  }
  const trimmed = sanitized.trim();
  return trimmed || ' '; // Ensure at least a space
};

// Type for table cell options
interface TableCellOptions {
  children: Paragraph[];
  shading?: {
    fill: string;
    type: 'clear';
  };
}

// Color mapping function for Word document colors
export const mapColorToWord = (
  color: string
):
  | 'red'
  | 'blue'
  | 'green'
  | 'yellow'
  | 'black'
  | 'white'
  | 'cyan'
  | 'magenta'
  | 'lightGray'
  | 'darkRed'
  | 'darkBlue'
  | 'darkGreen'
  | 'darkYellow'
  | 'darkCyan'
  | 'darkMagenta'
  | 'darkGray'
  | undefined => {
  const colorMap: {
    [key: string]:
      | 'red'
      | 'blue'
      | 'green'
      | 'yellow'
      | 'black'
      | 'white'
      | 'cyan'
      | 'magenta'
      | 'lightGray'
      | 'darkRed'
      | 'darkBlue'
      | 'darkGreen'
      | 'darkYellow'
      | 'darkCyan'
      | 'darkMagenta'
      | 'darkGray';
  } = {
    red: 'red',
    blue: 'blue',
    green: 'green',
    yellow: 'yellow',
    black: 'black',
    white: 'white',
    cyan: 'cyan',
    magenta: 'magenta',
    gray: 'lightGray',
    grey: 'lightGray',
    darkred: 'darkRed',
    darkblue: 'darkBlue',
    darkgreen: 'darkGreen',
    darkyellow: 'darkYellow',
    darkcyan: 'darkCyan',
    darkmagenta: 'darkMagenta',
    darkgray: 'darkGray',
    darkgrey: 'darkGray',
    lightgray: 'lightGray',
    lightgrey: 'lightGray',
    // Hex color mappings
    '#ff0000': 'red',
    '#0000ff': 'blue',
    '#00ff00': 'green',
    '#ffff00': 'yellow',
    '#000000': 'black',
    '#ffffff': 'white',
    '#00ffff': 'cyan',
    '#ff00ff': 'magenta',
    '#808080': 'lightGray',
    '#800000': 'darkRed',
    '#000080': 'darkBlue',
    '#008000': 'darkGreen',
    '#808000': 'darkYellow',
    '#008080': 'darkCyan',
    '#800080': 'darkMagenta',
    '#404040': 'darkGray',
    // RGB color mappings (common colors)
    'rgb(255,0,0)': 'red',
    'rgb(0,0,255)': 'blue',
    'rgb(0,255,0)': 'green',
    'rgb(255,255,0)': 'yellow',
    'rgb(0,0,0)': 'black',
    'rgb(255,255,255)': 'white',
    'rgb(0,255,255)': 'cyan',
    'rgb(255,0,255)': 'magenta',
    'rgb(128,128,128)': 'lightGray',
    'rgb(128,0,0)': 'darkRed',
    'rgb(0,0,128)': 'darkBlue',
    'rgb(0,128,0)': 'darkGreen',
    'rgb(128,128,0)': 'darkYellow',
    'rgb(0,128,128)': 'darkCyan',
    'rgb(128,0,128)': 'darkMagenta',
    'rgb(64,64,64)': 'darkGray',
    // Specific RGB colors
    'rgb(26,95,180)': 'blue',
    'rgb(198,70,0)': 'red'
  };

  const normalizedColor = color.toLowerCase().replace(/\s/g, '');
  return colorMap[normalizedColor] || undefined;
};

// Helper: Convert rgb() or hex to 6-digit hex string for docx
/**
 * Converts a CSS color string (e.g., named color, hex, or rgb()) to its hexadecimal representation.
 * @param {string | undefined} color - The CSS color string to convert. Can be a named color, RGB expression (e.g., "rgb(255, 0, 0)"), or hex color (e.g., "#FF0000"). If undefined, the function will return undefined.
 * @returns {string | undefined} The hexadecimal representation of the input color in uppercase format (without the leading "#"), or undefined if the input is invalid or cannot be converted.
 */
export function cssColorToHex(color: string | undefined): string | undefined {
  if (!color) return undefined;
  color = color.trim();
  // If already hex
  if (color.startsWith('#')) {
    let hex = color.replace('#', '').toUpperCase();
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((x) => x + x)
        .join('');
    }
    if (hex.length === 6) return hex;
    return undefined;
  }
  // If rgb()
  const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (rgbMatch) {
    return rgbMatch
      .slice(1, 4)
      .map((x) => parseInt(x).toString(16).padStart(2, '0').toUpperCase())
      .join('');
  }
  // If named color, fallback
  const named: Record<string, string> = {
    red: 'FF0000',
    blue: '0000FF',
    green: '00FF00',
    yellow: 'FFFF00',
    black: '000000',
    white: 'FFFFFF',
    cyan: '00FFFF',
    magenta: 'FF00FF',
    gray: '808080',
    grey: '808080',
    orange: 'FFA500'
  };
  return named[color.toLowerCase()] || undefined;
}

// Process HTML element and convert to Word document elements
export const processElement = (element: Element): DocxElement[] => {
  const children: DocxElement[] = [];

  switch (element.tagName.toLowerCase()) {
    case 'h1':
      children.push(
        new Paragraph({
          text: element.textContent || '',
          heading: HeadingLevel.HEADING_1
        })
      );
      break;
    case 'h2':
      children.push(
        new Paragraph({
          text: element.textContent || '',
          heading: HeadingLevel.HEADING_2
        })
      );
      break;
    case 'h3':
      children.push(
        new Paragraph({
          text: element.textContent || '',
          heading: HeadingLevel.HEADING_3
        })
      );
      break;
    case 'p':
      {
        // For desktop Word compatibility, simplify paragraph creation
        // Use textContent directly instead of complex TextRun structures
        const textContent = element.textContent;
        if (textContent) {
          const sanitized = sanitizeText(textContent);
          if (sanitized.trim().length > 0) {
            // Replace <br> and newlines with actual newlines in text
            // Split very long text into multiple paragraphs
            const maxLength = 8000;
            if (sanitized.length > maxLength) {
              for (let i = 0; i < sanitized.length; i += maxLength) {
                const chunk = sanitizeText(sanitized.substring(i, i + maxLength));
                if (chunk.trim().length > 0) {
                  children.push(new Paragraph({ text: chunk.trim() }));
                }
              }
            } else {
              children.push(new Paragraph({ text: sanitized.trim() }));
            }
          }
        }
        break;
      }
      // Skip truly empty paragraphs
      break;
    case 'ul':
      element.querySelectorAll('li').forEach((li) => {
        children.push(
          new Paragraph({
            text: li.textContent || '',
            bullet: { level: 0 }
          })
        );
      });
      break;
    case 'ol':
      element.querySelectorAll('li').forEach((li) => {
        children.push(
          new Paragraph({
            text: li.textContent || '',
            numbering: { reference: 'default-numbering', level: 0 }
          })
        );
      });
      break;
    case 'table':
      children.push(...processTableElement(element));
      break;
    case 'pre':
      children.push(
        new Paragraph({
          text: element.textContent || '',
          style: 'code'
        })
      );
      break;
    case 'hr':
      // Create a horizontal rule using a paragraph with separator
      children.push(
        new Paragraph({
          text: '',
          spacing: {
            after: 200,
            before: 200
          },
          border: {
            bottom: {
              color: '000000',
              space: 1,
              style: 'single',
              size: 6
            }
          }
        })
      );
      break;
    case 'br':
      // Create a line break by adding a paragraph with a space (Word doesn't like completely empty paragraphs)
      children.push(new Paragraph({ text: ' ' }));
      break;
    case 'div':
      // Process child elements of div
      element.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE && child.textContent?.trim()) {
          children.push(new Paragraph({ text: child.textContent }));
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          children.push(...processElement(child as Element));
        }
      });
      break;
    default:
      // Process child elements
      element.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE && child.textContent?.trim()) {
          children.push(new Paragraph({ text: child.textContent }));
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          children.push(...processElement(child as Element));
        }
      });
  }

  return children;
};

// Process table element specifically
export const processTableElement = (element: Element): DocxElement[] => {
  const tableRows: TableRow[] = [];

  // Handle Tiptap table structure with tbody
  const tbody = element.querySelector('tbody');
  const rows = tbody ? tbody.querySelectorAll('tr') : element.querySelectorAll('tr');

  rows.forEach((tr) => {
    const row: TableCell[] = [];
    tr.querySelectorAll('td, th').forEach((cell) => {
      // Extract text content from paragraph elements inside cells
      const cellText = cell.querySelector('p')?.textContent || cell.textContent || '';

      // Check for cell background color (from style attribute or data attribute)
      const cellStyle = cell.getAttribute('style') || '';
      const dataBackgroundColor = cell.getAttribute('data-background-color');
      const backgroundColor = dataBackgroundColor || cellStyle.match(/background-color:\s*([^;]+)/)?.[1];

      const cellOptions: TableCellOptions = {
        children: [new Paragraph({ text: cellText })]
      };

      // Add background color if present
      if (backgroundColor) {
        const hex = cssColorToHex(backgroundColor);
        if (hex) {
          cellOptions.shading = {
            fill: hex,
            type: 'clear'
          };
        }
      }

      row.push(new TableCell(cellOptions));
    });
    if (row.length > 0) {
      tableRows.push(new TableRow({ children: row }));
    }
  });

  if (tableRows.length > 0) {
    // Ensure all rows have the same number of cells
    const maxCells = Math.max(...tableRows.map((row) => row.cells.length), 0);
    if (maxCells > 0) {
      const normalizedRows = tableRows.map((row) => {
        const normalizedRow = [...row.cells];
        // Pad rows with empty cells if needed
        while (normalizedRow.length < maxCells) {
          normalizedRow.push(new TableCell({ children: [new Paragraph({ text: ' ' })] }));
        }
        return new TableRow({ children: normalizedRow });
      });

      return [
        new Table({
          rows: normalizedRows,
          width: {
            size: 100,
            type: WidthType.PERCENTAGE
          }
        })
      ];
    }
  }

  return [];
};
